import { Controller } from "@hotwired/stimulus"

// Interactive monthly-savings BAR chart.
//
// • Tap any bar  → callout pill jumps to that bar, the bar lights up amber,
//   others go back to emerald, the selected month label below bolds + tints amber.
//   The trend badge to the right of the pill updates with ▲ / ▼ vs previous month.
// • On connect:  bars rise from the baseline left-to-right with a bouncy spring,
//   then the callout settles into place above the best month — Cal-AI style.
//
// Markup lives in app/views/budgets/all_time_saved.html.erb
export default class extends Controller {
  static targets = [
    "dot", "monthLabel",
    "calloutGroup", "calloutRect", "calloutText",
    "trendTspan", "sepTspan", "amountTspan"
  ]

  static values = {
    plotW: Number,
    padX:  Number,
    vbW:   Number,
    bestIndex: Number,
    selectedIndex: Number
  }

  connect() {
    this.selectedIndex = this.hasSelectedIndexValue ? this.selectedIndexValue : this.bestIndexValue
    this._animateIn()

    // Turbo restores cached page snapshots without re-running connect().
    // Replay the entrance animation each time the user navigates back here.
    this._onTurboLoad   = () => this._animateIn()
    this._onBeforeCache = () => this._resetAnimationStyles()
    document.addEventListener("turbo:load",         this._onTurboLoad)
    document.addEventListener("turbo:render",       this._onTurboLoad)
    document.addEventListener("turbo:before-cache", this._onBeforeCache)
  }

  disconnect() {
    if (this._onTurboLoad) {
      document.removeEventListener("turbo:load",   this._onTurboLoad)
      document.removeEventListener("turbo:render", this._onTurboLoad)
    }
    if (this._onBeforeCache) {
      document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    }
    if (this._animTimers) this._animTimers.forEach(t => clearTimeout(t))
  }

  // ── Click handler ───────────────────────────────────────────────
  selectDot(event) {
    const group = event.currentTarget
    const idx = parseInt(group.dataset.index, 10)
    if (Number.isNaN(idx)) return
    this.selectedIndex = idx
    this._renderSelection(true)

    // Light haptic via the existing bridge component.
    window.dispatchEvent(new CustomEvent("bridge:haptic", { detail: { style: "light" } }))
  }

  // ── Animations ──────────────────────────────────────────────────
  // Cal-AI-style: bars rise from baseline, staggered left-to-right with a
  // bouncy spring; the callout settles last with a spring of its own.
  _animateIn() {
    // Debounce: connect() and turbo:load/turbo:render can fire within milliseconds of
    // each other on navigation, which used to cause the bars to bounce twice.
    const now = (typeof performance !== "undefined") ? performance.now() : Date.now()
    if (this._lastAnimateAt && (now - this._lastAnimateAt) < 250) return
    this._lastAnimateAt = now

    if (this._animTimers) this._animTimers.forEach(t => clearTimeout(t))
    this._animTimers = []

    this._resetAnimationStyles()

    // Bars start collapsed (scaleY 0 from the bottom).
    const bars = this.dotTargets.map(g => g.querySelector("[data-role=bar]")).filter(Boolean)
    bars.forEach((bar) => {
      bar.style.transformBox    = "fill-box"
      bar.style.transformOrigin = "50% 100%"
      bar.style.transform       = "scaleY(0)"
    })

    // Callout starts hidden + slightly raised.
    if (this.hasCalloutGroupTarget) {
      const cg = this.calloutGroupTarget
      cg.style.transformBox    = "fill-box"
      cg.style.transformOrigin = "center"
      cg.style.opacity         = "0"
      cg.style.transform       = "translateY(6px)"
    }

    // Force a reflow so the browser paints the "before" state before transitions
    // attach. Without this, repeat runs (turbo cache restore) snap to end state.
    if (bars[0]) void bars[0].getBoundingClientRect()

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Bars rise sequentially.
        const stride = 90
        const springEase = "cubic-bezier(0.34, 1.56, 0.64, 1)" // overshoots then settles
        bars.forEach((bar, i) => {
          const delay = 80 + i * stride
          bar.style.transition = `transform 620ms ${springEase} ${delay}ms`
          bar.style.transform  = "scaleY(1)"
        })

        // Callout settles after all bars have started rising.
        if (this.hasCalloutGroupTarget) {
          const settle = 80 + bars.length * stride + 220
          this._animTimers.push(setTimeout(() => {
            this.calloutGroupTarget.style.transition = "opacity 360ms ease, transform 360ms cubic-bezier(0.34,1.56,0.64,1)"
            this.calloutGroupTarget.style.opacity    = "1"
            this.calloutGroupTarget.style.transform  = "translateY(0)"
            this._renderSelection()
          }, settle))
        }
      })
    })
  }

  _resetAnimationStyles() {
    this.dotTargets.forEach(g => {
      const bar = g.querySelector("[data-role=bar]")
      if (bar) {
        bar.style.transition = "none"
        bar.style.transform  = ""
        bar.style.filter     = ""
      }
    })
    if (this.hasCalloutGroupTarget) {
      this.calloutGroupTarget.style.transition = "none"
      this.calloutGroupTarget.style.opacity    = ""
      this.calloutGroupTarget.style.transform  = ""
    }
  }

  // Amber bloom rendered as a CSS filter so it can cross-fade on selection.
  static GLOW = "drop-shadow(0 3px 6px rgba(245,158,11,0.45)) drop-shadow(0 1px 2px rgba(251,191,36,0.55))"

  // ── Rendering selection state ──────────────────────────────────
  // pop=true → the newly selected bar does a quick spring pop (tap feedback).
  _renderSelection(pop = false) {
    const idx = this.selectedIndex
    const GLOW = this.constructor.GLOW

    // Bar colour + glow: active = amber gradient with a soft amber bloom that
    // lifts it off the surface; inactive = flat emerald, no glow. The glow is a
    // CSS drop-shadow so it cross-fades, and the active bar pops on tap.
    this.dotTargets.forEach((g, i) => {
      const bar = g.querySelector("[data-role=bar]")
      if (!bar) return
      const active = i === idx
      bar.setAttribute("fill", active ? "url(#atsBarBest)" : "url(#atsBarFill)")
      bar.style.transformBox    = "fill-box"
      bar.style.transformOrigin = "50% 100%"

      if (active && pop) {
        bar.style.transition = "filter 280ms ease, transform 140ms ease-out"
        bar.style.filter     = GLOW
        bar.style.transform  = "scaleY(1.06)"
        const t = setTimeout(() => {
          bar.style.transition = "filter 280ms ease, transform 260ms cubic-bezier(0.34,1.56,0.64,1)"
          bar.style.transform  = "scaleY(1)"
        }, 140)
        ;(this._animTimers ||= []).push(t)
      } else if (active) {
        bar.style.transition = "filter 280ms ease"
        bar.style.filter     = GLOW
        bar.style.transform  = "scaleY(1)"
      } else {
        bar.style.transition = "filter 280ms ease"
        bar.style.filter     = "none"
      }
    })

    // Month label: active = bold amber
    this.monthLabelTargets.forEach((lbl, i) => {
      if (i === idx) {
        lbl.setAttribute("fill", "rgb(245 158 11)")
        lbl.setAttribute("font-weight", "900")
      } else {
        lbl.setAttribute("fill", "currentColor")
        lbl.setAttribute("font-weight", "800")
      }
    })

    // Reposition the callout above the selected bar
    const dot = this.dotTargets[idx]
    if (!dot || !this.hasCalloutGroupTarget) return

    const cx     = parseFloat(dot.dataset.x)
    const barTop = parseFloat(dot.dataset.y)
    const label  = dot.dataset.label
    const dir    = dot.dataset.deltaDir // "up", "down", or "" / undefined
    const pct    = dot.dataset.deltaPct // string of integer or ""

    // Build the combined pill text: "▲ 28% | +$389.35" — or just "+$389.35" for first month.
    const hasTrend = (dir === "up" || dir === "down")
    const glyph    = dir === "up" ? "▲" : dir === "down" ? "▼" : ""
    const trendStr = hasTrend ? `${glyph} ${pct ? Math.abs(parseInt(pct, 10)) + "%" : ""}`.trim() : ""
    const color    = dir === "up"   ? "rgb(5 150 105)" :
                     dir === "down" ? "rgb(220 38 38)" :
                                      "rgb(217 119 6)"

    // Update tspans
    if (this.hasTrendTspanTarget) {
      this.trendTspanTarget.textContent = trendStr
      this.trendTspanTarget.setAttribute("fill", color)
      this.trendTspanTarget.style.display = hasTrend ? "" : "none"
    }
    if (this.hasSepTspanTarget) {
      this.sepTspanTarget.textContent = hasTrend ? "  |  " : ""
      this.sepTspanTarget.style.display = hasTrend ? "" : "none"
    }
    if (this.hasAmountTspanTarget) {
      this.amountTspanTarget.textContent = label
    }

    // Estimate combined width: trend chars + separator + amount + side padding.
    const combinedChars = trendStr.length + (hasTrend ? 3 : 0) + label.length
    const lbl_w = Math.max(combinedChars * 6.4 + 28, 78)
    const lbl_x = Math.min(Math.max(cx - lbl_w / 2.0, this.padXValue), this.vbWValue - this.padXValue - lbl_w)
    const lbl_y = Math.max(barTop - 26, 2)

    if (this.hasCalloutRectTarget) {
      this.calloutRectTarget.setAttribute("x", lbl_x.toFixed(1))
      this.calloutRectTarget.setAttribute("y", lbl_y.toFixed(1))
      this.calloutRectTarget.setAttribute("width", lbl_w.toFixed(1))
    }
    if (this.hasCalloutTextTarget) {
      this.calloutTextTarget.setAttribute("x", (lbl_x + lbl_w / 2.0).toFixed(1))
      this.calloutTextTarget.setAttribute("y", (lbl_y + 13.5).toFixed(1))
    }
  }
}
